import { useState } from "react";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Signed-in user changes their own password (any portal), verified by a
// code emailed to their account address — the current password isn't
// needed, so someone who forgot it can still change it.
export default function ChangePasswordDialog({ open, onOpenChange }) {
  const { user } = useAuth();
  const [sentTo, setSentTo] = useState(null);
  const [code, setCode] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const close = (o) => {
    if (!o) { setSentTo(null); setCode(""); setNext(""); setConfirm(""); }
    onOpenChange(o);
  };

  const sendCode = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password/request-code");
      setSentTo(data.sent_to);
      setCode("");
      toast.success(`Code sent to ${data.sent_to}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send the code");
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error("New password must be at least 8 characters");
    if (next !== confirm) return toast.error("The new passwords don't match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { code: code.trim(), new_password: next });
      toast.success("Password changed");
      close(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally {
      setBusy(false);
    }
  };

  const input = "w-full h-10 px-3 rounded-lg border border-slate-200 text-sm outline-none focus:border-sky-400";
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="change-password-modal">
        <DialogHeader><DialogTitle>Change Password</DialogTitle></DialogHeader>
        {!sentTo ? (
          <div className="space-y-3 mt-2">
            <p className="text-[13px] text-slate-600">
              We'll email a 6-digit verification code to <span className="font-semibold">{user?.email}</span>. You don't need your current password.
            </p>
            <button onClick={sendCode} disabled={busy} data-testid="send-password-code-button" className="w-full h-10 rounded-lg bg-sky-500 text-white text-[13px] font-semibold disabled:opacity-60 flex items-center justify-center gap-2">
              <Mail size={14} /> {busy ? "Sending…" : "Email me a code"}
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3 mt-2">
            <p className="text-[12.5px] text-slate-500">
              Enter the code sent to <span className="font-semibold text-slate-700">{sentTo}</span>. It expires in 10 minutes.
            </p>
            <div>
              <label className="text-[13px] font-medium">Verification code</label>
              <input inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} data-testid="password-code-input" className={`mt-1.5 ${input} tracking-[0.4em] font-mono`} />
            </div>
            <div>
              <label className="text-[13px] font-medium">New password</label>
              <input type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} data-testid="new-password-input" className={`mt-1.5 ${input}`} />
              <p className="text-[11.5px] text-slate-400 mt-1">At least 8 characters.</p>
            </div>
            <div>
              <label className="text-[13px] font-medium">Confirm new password</label>
              <input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="confirm-password-input" className={`mt-1.5 ${input}`} />
            </div>
            <button type="submit" disabled={busy || code.length !== 6 || !next || !confirm} data-testid="confirm-change-password-button" className="w-full h-10 rounded-lg bg-sky-500 text-white text-[13px] font-semibold disabled:opacity-60">
              {busy ? "Saving…" : "Change Password"}
            </button>
            <button type="button" onClick={sendCode} disabled={busy} className="w-full text-[12.5px] font-semibold text-sky-600 disabled:opacity-60">
              Didn't get it? Send a new code
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
